import Phaser from 'phaser';

import { CHECKPOINT_SCORE_BAND_COUNT } from '../objects/StrokeEvaluator';

type RewardStroke = {
	path: Phaser.Curves.Path;
	length: number;
	checkpoints: { index: number; distance: number }[];
};

type StrokeRewardRendererConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	textureKey: string;
	strokes: RewardStroke[];
};

const REWARD_DECORATION = { minScale: 0.6, maxScale: 1 };
const REWARD_ANIMATION = { duration: 320, ease: 'Back.Out', easeParams: [1.7], maxDelay: 120 };

export class StrokeRewardRenderer {
	private readonly container: Phaser.GameObjects.Container;
	private readonly revealed = new Set<string>();

	constructor(private readonly config: StrokeRewardRendererConfig) {
		this.container = config.scene.add.container(0, 0);
		config.parent.add(this.container);
	}

	reveal(strokeIndex: number, checkpointIndex: number, score: number) {
		const { scene, textureKey, strokes } = this.config;
		const stroke = strokes[strokeIndex];
		const index = stroke.checkpoints.findIndex((checkpoint) => checkpoint.index === checkpointIndex);
		const key = `${strokeIndex}:${checkpointIndex}`;
		if (index < 0 || this.revealed.has(key) || !scene.textures.exists(textureKey)) return;
		this.revealed.add(key);
		const checkpoint = stroke.checkpoints[index];
		const previous = stroke.checkpoints[index - 1];
		const next = stroke.checkpoints[index + 1];
		const start = previous ? (previous.distance + checkpoint.distance) / 2 : 0;
		const end = next ? (checkpoint.distance + next.distance) / 2 : stroke.length;
		const spacing = scene.textures.getFrame(textureKey).realWidth;
		const count = Math.max(1, Math.round(stroke.length / spacing));
		const minimumScore = 1 / CHECKPOINT_SCORE_BAND_COUNT;
		const quality = Phaser.Math.Clamp((score - minimumScore) / (1 - minimumScore), 0, 1);
		const scale = Phaser.Math.Linear(REWARD_DECORATION.minScale, REWARD_DECORATION.maxScale, quality);
		for (let index = 0; index < count; index++) {
			const distance = ((index + 0.5) / count) * stroke.length;
			if (distance < start || distance >= end) continue;
			const point = stroke.path.getPoint(stroke.length > 0 ? distance / stroke.length : 0);
			const decoration = scene.add.image(point.x, point.y, textureKey);
			decoration.setAngle(Phaser.Math.FloatBetween(-180, 180)).setScale(0);
			this.container.add(decoration);
			decoration.once(Phaser.GameObjects.Events.DESTROY, () => scene.tweens.killTweensOf(decoration));
			scene.tweens.add({
				targets: decoration,
				scale,
				duration: REWARD_ANIMATION.duration,
				ease: REWARD_ANIMATION.ease,
				easeParams: REWARD_ANIMATION.easeParams,
				delay: Phaser.Math.FloatBetween(0, REWARD_ANIMATION.maxDelay),
			});
		}
	}

	clear() {
		this.container.removeAll(true);
		this.revealed.clear();
	}
}
